//----------------------------------------
// カメラ解像度設定
//----------------------------------------
// スキャン時のカメラ解像度（FHD / HD）を保持する。
// 優先順位・保存方法・本番での固定は ScanSDK切替（scanSdkSetting.js）と同じ方式に揃えている。
// 優先順位： URLパラメータ(?res=) > localStorage > 既定値(FHD)
//
// ※低スペック端末では解像度が体感速度に直結する。
// 　barkoder はデコードのたびに全画素の getImageData を行うため、
// 　FHD(1920x1080=約2.07Mpx) と HD(1280x720=約0.92Mpx) で1回あたりの負荷が約2.25倍違う。
// 　詳細は docs/scan-sdk.md の「負荷設定の考え方」を参照

export const CAMERA_RESOLUTION_FHD = "FHD";
export const CAMERA_RESOLUTION_HD = "HD";

//選択肢（設定画面の並び順
export const CAMERA_RESOLUTION_LIST = [CAMERA_RESOLUTION_FHD, CAMERA_RESOLUTION_HD];

//既定の解像度（従来の挙動と同じ
export const CAMERA_RESOLUTION_DEFAULT = CAMERA_RESOLUTION_FHD;

//localStorageキー
const STORAGE_KEY = "regigo_camera_resolution";

//本番ドメイン（解像度は選択制にせず、envの指定で固定する
const FIXED_RESOLUTION_DOMAIN = "miniapp.regigo.jp";

//解像度名として正しいか
//※URLパラメータやenvは大文字小文字を問わない（?res=hd でも通す
function normalize(value) {
	if (typeof value !== "string") {
		return null;
	}
	const upper = value.toUpperCase();
	return CAMERA_RESOLUTION_LIST.indexOf(upper) !== -1 ? upper : null;
}

//----------------------------------------
//解像度固定（本番）かどうか
//----------------------------------------
// true の場合：
//   ・解像度は REACT_APP_CAMERA_RESOLUTION で固定（URLパラメータ・localStorageは無視
//   ・設定画面の解像度選択は非表示

export function isCameraResolutionFixed() {
	if (typeof window === "undefined") {
		return false;
	}
	return window.location.host === FIXED_RESOLUTION_DOMAIN;
}

//envで指定された解像度（未設定・不正値は既定値
function getFixedCameraResolution() {
	const value = normalize(process.env.REACT_APP_CAMERA_RESOLUTION);
	if (value) {
		return value;
	}
	console.log("【解像度】REACT_APP_CAMERA_RESOLUTION が未設定または不正のため既定値を使用: " + CAMERA_RESOLUTION_DEFAULT);
	return CAMERA_RESOLUTION_DEFAULT;
}

//----------------------------------------
//解像度取得
//----------------------------------------

export function getCameraResolution() {
	if (typeof window === "undefined") {
		return CAMERA_RESOLUTION_DEFAULT;
	}

	//本番はenvで固定（URLパラメータ・localStorageは参照しない
	if (isCameraResolutionFixed()) {
		return getFixedCameraResolution();
	}

	//URLパラメータ（?res=hd など）優先：指定時はlocalStorageにも保存
	try {
		const param = normalize(new URLSearchParams(window.location.search).get("res"));
		if (param) {
			saveCameraResolution(param);
			return param;
		}
	} catch (e) {
		console.log("getCameraResolution: urlParams error, " + e);
	}

	//localStorage
	try {
		const stored = normalize(window.localStorage.getItem(STORAGE_KEY));
		if (stored) {
			return stored;
		}
	} catch (e) {
		console.log("getCameraResolution: localStorage error, " + e);
	}

	return CAMERA_RESOLUTION_DEFAULT;
}

//----------------------------------------
//解像度保存
//----------------------------------------

export function saveCameraResolution(resolution) {
	//本番はenv固定のため保存しない
	if (isCameraResolutionFixed()) {
		return getFixedCameraResolution();
	}

	const value = normalize(resolution);
	if (!value) {
		return CAMERA_RESOLUTION_DEFAULT;
	}

	try {
		window.localStorage.setItem(STORAGE_KEY, value);
	} catch (e) {
		console.log("saveCameraResolution: localStorage error, " + e);
	}
	return value;
}

//----------------------------------------
//解像度の画素数
//----------------------------------------
// 各SDKへ渡す幅・高さ。SDKごとに指定方法が違うため、値だけをここで一元管理する。

export function getCameraResolutionSize() {
	return getCameraResolution() === CAMERA_RESOLUTION_HD
		? { width: 1280, height: 720 }
		: { width: 1920, height: 1080 };
}

//----------------------------------------
//barkoder のデコード頻度
//----------------------------------------
// ※barkoderはメインスレッドで「全画素の getImageData → wasmデコード」を
// 　setInterval(1000/dps) ごとに同期実行する。上限を超えるとプレビューがコマ落ちする。
//
// 10 → 15 → 20 と段階的に引き上げた（2026-08-02）。実機でコマ落ちが出ないことを確認済み。
// D-19 で 15→10 に下げたのは `DecodingSpeed:Slow` と併用していた時期の話。
//
//   FHD … 20回/秒 × 約2.07Mpx ＝ 約41.4Mpx/秒
//   HD  … 20回/秒 × 約0.92Mpx ＝ 約18.4Mpx/秒
//
// D-19時点はFHD＋20.7Mpx/秒でコマ落ちしていたので、いまはその2倍の負荷で動いている。
// コマ落ちを決めていたのはデコード回数ではなく DecodingSpeed だった。
//
// 30まで上げても意味は無い（カメラが30fpsのため同じフレームを二度デコードするだけ）。
// コマ落ちが再発したら 15 に戻す。FHDだけが厳しい場合は解像度で分岐させ直す。
//
// 現状は解像度によらず同じ値だが、解像度ごとに変える可能性があるため関数のまま残す。

export function getBarkoderDpsLimit() {
	return 20;
}
